import React from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface ReviewData {
  step1: any;
  step3: any;
}

interface Step4Props {
  data: ReviewData;
  onSubmit: () => void;
  isSubmitting: boolean;
}

const Step4Review: React.FC<Step4Props> = ({ data, onSubmit, isSubmitting }) => {
  const { step1, step3 } = data;

  const getTotalQuestions = () => {
    let total = 0;
    let completed = 0;

    Object.keys(step3).forEach(section => {
      const sectionData = step3[section];
      const questions = Object.keys(sectionData);
      total += questions.length;
      completed += questions.filter(qId => sectionData[qId]?.answer || sectionData[qId]?.genderMale !== undefined || sectionData[qId]?.genderFemale !== undefined).length;
    });

    return { total, completed };
  };

  const { total, completed } = getTotalQuestions();
  const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

  const getMajorIssues = () => {
    const issues: string[] = [];

    // Check for concerning answers
    Object.keys(step3).forEach(section => {
      const sectionData = step3[section];
      Object.keys(sectionData).forEach(qId => {
        const answer = sectionData[qId];
        if (answer?.answer === 'No' && ['B1', 'D1'].includes(qId)) {
          issues.push(`${qId}: Late start time`);
        }
        if (answer?.answer === 'Yes' && ['B6', 'C2', 'C3', 'E2'].includes(qId)) {
          issues.push(`${qId}: ${answer.details || 'Issue reported'}`);
        }
        if (answer?.answer === 'Poor' && qId === 'E1') {
          issues.push('Poor overall rating');
        }
      });
    });

    return issues;
  };

  // Structured party breakdown for review UI
  const getPartyRepresentatives = (): Array<{ partyName: string; male: number; female: number; total: number }> => {
    try {
      const partyData = step3.question2?.['2c_parties']?.details ||
        step3.question2?.['11c_parties']?.details ||
        step3.question2?.['22c_parties']?.details ||
        step3.question2?.['22e_parties']?.details;
      if (!partyData) return [];

      const parties = JSON.parse(partyData);
      return parties
        .filter((party: any) => party && party.partyName)
        .map((party: any) => {
          const male = Number(party.maleRepresentatives ?? party.genderMale ?? 0);
          const female = Number(party.femaleRepresentatives ?? party.genderFemale ?? 0);
          const total = (party.numberOfRepresentatives !== undefined)
            ? Number(party.numberOfRepresentatives)
            : male + female;
          return { partyName: party.partyName, male, female, total };
        });
    } catch {
      return [];
    }
  };

  const formatAnswerValue = (answer: any) => {
    if (!answer) return 'Not answered';

    // Gender-specific rendering
    const hasGender = answer.genderMale !== undefined || answer.genderFemale !== undefined;
    if (hasGender) {
      const male = Number(answer.genderMale || 0);
      const female = Number(answer.genderFemale || 0);
      const total = male + female;
      const prefix = answer.answer ? `${answer.answer} - ` : '';
      let extra = '';
      if (answer.comments) extra += ` (Comments: ${answer.comments})`;
      if (answer.details) extra += ` (Details: ${answer.details})`;
      if (answer.explanation) extra += ` (Explanation: ${answer.explanation})`;
      return `${prefix}Male: ${male}, Female: ${female} (Total: ${total})${extra}`;
    }

    if (!answer?.answer) return 'Not answered';

    let result = answer.answer;
    if (answer.comments) result += ` (Comments: ${answer.comments})`;
    if (answer.details) result += ` (Details: ${answer.details})`;
    if (answer.explanation) result += ` (Explanation: ${answer.explanation})`;
    if (answer.actualStartTime) result += ` (Actual time: ${answer.actualStartTime})`;
    if (answer.invalidBallotCount !== undefined) result += ` (Count: ${answer.invalidBallotCount})`;

    return result;
  };

  const majorIssues = getMajorIssues();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-4 text-xl font-semibold text-gray-900">
          Review & Submit Report
        </h2>
        <p className="mb-6 text-gray-600">
          Please review all information before submitting your observer report.
        </p>
      </div>

      {/* Modern Completion Status */}
      <div className="p-6 border border-blue-100 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="p-3 mr-4 bg-blue-500 rounded-xl">
              <div className="text-xl text-white">📊</div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Completion Status</h3>
              <p className="text-sm text-gray-600">Your progress across all sections</p>
            </div>
          </div>
          <div className="text-right">
            <div className="mb-1 text-3xl font-bold text-blue-600">{completionRate}%</div>
            <div className="text-sm text-gray-600">Complete</div>
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Progress</span>
            <span className="text-sm text-gray-600">{completed} of {total} questions</span>
          </div>
          <div className="w-full h-3 overflow-hidden bg-blue-200 rounded-full">
            <div
              className="relative h-3 transition-all duration-500 rounded-full bg-gradient-to-r from-blue-500 to-purple-600"
              style={{ width: `${completionRate}%` }}
            >
              <div className="absolute inset-0 bg-white bg-opacity-30 animate-pulse"></div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-center">
          <div className="p-3 rounded-lg bg-white/50">
            <div className="text-lg font-bold text-green-600">{completed}</div>
            <div className="text-xs text-gray-600">Completed</div>
          </div>
          <div className="p-3 rounded-lg bg-white/50">
            <div className="text-lg font-bold text-orange-600">{total - completed}</div>
            <div className="text-xs text-gray-600">Remaining</div>
          </div>
        </div>
      </div>      {/* Major Issues Alert - Modern Design */}
      {majorIssues.length > 0 && (
        <div className="p-6 border border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl">
          <div className="flex items-start">
            <div className="p-2 mr-4 bg-yellow-100 rounded-lg">
              <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h4 className="mb-2 text-lg font-semibold text-yellow-900">Issues Identified</h4>
              <p className="mb-4 text-sm text-yellow-800">The following issues require attention in your report:</p>
              <div className="space-y-2">
                {majorIssues.map((issue, index) => (
                  <div key={index} className="flex items-center p-3 rounded-lg bg-white/70">
                    <div className="w-2 h-2 mr-3 bg-yellow-500 rounded-full"></div>
                    <span className="text-sm font-medium text-yellow-900">{issue}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 1 Review - Modern Design */}
      <div className="overflow-hidden bg-white border border-gray-100 shadow-sm rounded-xl">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50">
          <h3 className="flex items-center text-lg font-semibold text-gray-900">
            <div className="p-2 mr-3 bg-green-100 rounded-lg">
              <CheckCircleIcon className="w-5 h-5 text-green-600" />
            </div>
            Location & Observer Information
          </h3>
          <p className="mt-1 text-sm text-gray-600">Your assigned location and profile details</p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="p-4 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100">
              <div className="flex items-center mb-2">
                <span className="mr-2 text-lg">⏰</span>
                <span className="text-sm font-medium text-gray-900">Timeline</span>
              </div>
              <p className="text-sm text-gray-700">
                {step1.timeline ? step1.timeline.charAt(0).toUpperCase() + step1.timeline.slice(1).replace('_', ' ') : 'Not specified'}
              </p>
            </div>

            <div className="p-4 rounded-lg bg-gradient-to-br from-purple-50 to-purple-100">
              <div className="flex items-center mb-2">
                <span className="mr-2 text-lg">📍</span>
                <span className="text-sm font-medium text-gray-900">District</span>
              </div>
              <p className="text-sm text-gray-700">{step1.district || 'Not specified'}</p>
            </div>

            <div className="p-4 rounded-lg bg-gradient-to-br from-indigo-50 to-indigo-100">
              <div className="flex items-center mb-2">
                <span className="mr-2 text-lg">🏛️</span>
                <span className="text-sm font-medium text-gray-900">Constituency</span>
              </div>
              <p className="text-sm text-gray-700">{step1.constituency || 'Not specified'}</p>
            </div>

            <div className="p-4 rounded-lg bg-gradient-to-br from-teal-50 to-teal-100">
              <div className="flex items-center mb-2">
                <span className="mr-2 text-lg">🏘️</span>
                <span className="text-sm font-medium text-gray-900">Ward</span>
              </div>
              <p className="text-sm text-gray-700">{step1.ward || 'Not specified'}</p>
            </div>

            <div className="p-4 rounded-lg bg-gradient-to-br from-orange-50 to-orange-100">
              <div className="flex items-center mb-2">
                <span className="mr-2 text-lg">🗳️</span>
                <span className="text-sm font-medium text-gray-900">Voting Center</span>
              </div>
              <p className="text-sm text-gray-700">{step1.votingCenter || 'Not specified'}</p>
            </div>

            <div className="p-4 rounded-lg bg-gradient-to-br from-green-50 to-green-100">
              <div className="flex items-center mb-2">
                <span className="mr-2 text-lg">👤</span>
                <span className="text-sm font-medium text-gray-900">Observer</span>
              </div>
              <p className="text-sm text-gray-700">{step1.observerName || 'Not specified'}</p>
            </div>

            <div className="p-4 rounded-lg bg-gradient-to-br from-yellow-50 to-yellow-100">
              <div className="flex items-center mb-2">
                <span className="mr-2 text-lg">📱</span>
                <span className="text-sm font-medium text-gray-900">Contact</span>
              </div>
              <p className="text-sm text-gray-700">{step1.observerContact || 'Not specified'}</p>
            </div>

            <div className="p-4 rounded-lg bg-gradient-to-br from-pink-50 to-pink-100">
              <div className="flex items-center mb-2">
                <span className="mr-2 text-lg">🏢</span>
                <span className="text-sm font-medium text-gray-900">Organization</span>
              </div>
              <p className="text-sm text-gray-700">{step1.observerOrganization || 'Not specified'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Political Party Representatives - Modern Design */}
      <div className="overflow-hidden bg-white border border-gray-100 shadow-sm rounded-xl">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-violet-50">
          <h3 className="flex items-center text-lg font-semibold text-gray-900">
            <div className="p-2 mr-3 bg-purple-100 rounded-lg">
              <CheckCircleIcon className="w-5 h-5 text-purple-600" />
            </div>
            Political Party Representatives
          </h3>
          <p className="mt-1 text-sm text-gray-600">Party representatives present at the polling station</p>
        </div>

        <div className="p-6">
          {getPartyRepresentatives().length > 0 ? (
            <div>
              <div className="flex items-center justify-between p-4 mb-4 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center">
                  <span className="mr-3 text-2xl">👥</span>
                  <div>
                    <div className="font-semibold text-gray-900">Total Representatives</div>
                    <div className="text-sm text-gray-600">Across all political parties</div>
                  </div>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {(() => {
                    try {
                      const parties = getPartyRepresentatives();
                      return parties.reduce((sum, p) => sum + (Number(p.total) || 0), 0);
                    } catch {
                      return 0;
                    }
                  })()}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {getPartyRepresentatives().map((party, index) => (
                  <div key={index} className="flex items-center justify-between p-3 transition-colors duration-200 rounded-lg bg-gray-50 hover:bg-gray-100">
                    <div className="flex items-center">
                      <div className="w-3 h-3 mr-3 bg-blue-500 rounded-full"></div>
                      <span className="text-sm font-medium text-gray-900">{party.partyName}</span>
                    </div>
                    <span className="text-xs font-medium text-gray-700">
                      Male: {party.male} • Female: {party.female} • Total: {party.total}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center">
              <div className="mb-3 text-4xl text-gray-400">🏛️</div>
              <h4 className="mb-2 font-medium text-gray-900">No Political Parties</h4>
              <p className="text-sm text-gray-600">No political party representatives have been recorded for this session.</p>
            </div>
          )}
        </div>
      </div>      {/* Step 3 Review - Modern Card Design */}
      <div className="overflow-hidden bg-white border border-gray-100 shadow-sm rounded-xl">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
          <h3 className="flex items-center text-lg font-semibold text-gray-900">
            <div className="p-2 mr-3 bg-blue-100 rounded-lg">
              <CheckCircleIcon className="w-5 h-5 text-blue-600" />
            </div>
            Checklist Responses Summary
          </h3>
          <p className="mt-1 text-sm text-gray-600">Review all your responses before submitting</p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {['question1', 'question2', 'question3', 'question4', 'question5', 'question6', 'question7', 'question8', 'question9'].map((questionSection, index) => {
              const sectionTitles = [
                'Station Setup & Initial Procedures',
                'Political Party Representatives',
                'Security & Environment',
                'Materials and Equipment',
                'Voter Verification',
                'Voting Process',
                'Operations and Accessibility',
                'Priority Groups',
                'Observers & Complaints'
              ];

              const sectionIcons = [
                '🏛️', '🗳️', '🛡️', '📦', '✅', '🗃️', '🔄', '👥', '👁️'
              ];

              const sectionData = step3[questionSection] || {};
              const answeredQuestions = Object.keys(sectionData).filter(qId => sectionData[qId]?.answer);
              const totalQuestions = Object.keys(sectionData).length;
              const completionPercentage = totalQuestions > 0 ? Math.round((answeredQuestions.length / totalQuestions) * 100) : 0;

              return (
                <div key={questionSection} className="p-4 transition-colors duration-200 rounded-lg bg-gray-50 hover:bg-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <span className="mr-2 text-xl">{sectionIcons[index]}</span>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Question {index + 1}</h4>
                        <p className="text-xs text-gray-600">{sectionTitles[index]}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {answeredQuestions.length}/{totalQuestions}
                      </div>
                      <div className="text-xs text-gray-500">completed</div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-600">Progress</span>
                      <span className="text-xs font-medium text-gray-900">{completionPercentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all duration-300 ${completionPercentage === 100 ? 'bg-green-500' :
                            completionPercentage > 50 ? 'bg-blue-500' :
                              completionPercentage > 0 ? 'bg-yellow-500' : 'bg-gray-300'
                          }`}
                        style={{ width: `${completionPercentage}%` }}
                      />
                    </div>
                  </div>

                  {answeredQuestions.length > 0 ? (
                    <div className="space-y-2">
                      <div className="mb-2 text-xs font-medium text-gray-700">Recent responses:</div>
                      {answeredQuestions.slice(0, 3).map(qId => (
                        <div key={qId} className="p-2 bg-white border-blue-200 rounded border-l-3">
                          <div className="text-xs font-medium text-gray-800">{qId}</div>
                          <div className="text-xs text-gray-600 truncate">
                            {formatAnswerValue(sectionData[qId])}
                          </div>
                        </div>
                      ))}
                      {answeredQuestions.length > 3 && (
                        <div className="py-1 text-xs text-center text-gray-500">
                          +{answeredQuestions.length - 3} more responses
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="py-4 text-center">
                      <div className="mb-2 text-2xl text-gray-400">📝</div>
                      <p className="text-xs text-gray-500">No questions answered yet</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Final Validation - Modern Design */}
      <div className="overflow-hidden bg-white border border-gray-100 shadow-sm rounded-xl">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-slate-50">
          <h4 className="flex items-center font-semibold text-gray-900">
            <div className="p-2 mr-3 bg-gray-100 rounded-lg">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            Final Verification
          </h4>
          <p className="mt-1 text-sm text-gray-600">Please confirm all information is accurate before submitting</p>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            <label className="flex items-center p-4 transition-colors duration-200 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <input type="checkbox" className="w-5 h-5 mr-4 text-blue-600 rounded" defaultChecked />
              <div>
                <div className="font-medium text-gray-900">Location Information</div>
                <div className="text-sm text-gray-600">All location details are accurate and complete</div>
              </div>
            </label>

            <label className="flex items-center p-4 transition-colors duration-200 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <input type="checkbox" className="w-5 h-5 mr-4 text-blue-600 rounded" defaultChecked />
              <div>
                <div className="font-medium text-gray-900">Party Representatives</div>
                <div className="text-sm text-gray-600">Political party representative count is correct</div>
              </div>
            </label>

            <label className="flex items-center p-4 transition-colors duration-200 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <input type="checkbox" className="w-5 h-5 mr-4 text-blue-600 rounded" defaultChecked />
              <div>
                <div className="font-medium text-gray-900">Checklist Responses</div>
                <div className="text-sm text-gray-600">All questions have been answered truthfully and accurately</div>
              </div>
            </label>

            <label className="flex items-center p-4 transition-colors duration-200 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <input type="checkbox" className="w-5 h-5 mr-4 text-blue-600 rounded" defaultChecked />
              <div>
                <div className="font-medium text-gray-900">Submission Ready</div>
                <div className="text-sm text-gray-600">Ready to submit this observer report</div>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Modern Submit Button */}
      <div className="p-8 text-center bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
        <div className="mb-4">
          <div className="mb-2 text-4xl">🚀</div>
          <h3 className="mb-2 text-xl font-semibold text-gray-900">Ready to Submit</h3>
          <p className="text-sm text-gray-600">Your observer report is ready for submission</p>
        </div>

        <button
          onClick={onSubmit}
          disabled={isSubmitting || completionRate < 80}
          className={`
            px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105
            ${isSubmitting || completionRate < 80
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg shadow-blue-500/25'
            }
          `}
        >
          {isSubmitting ? (
            <div className="flex items-center">
              <div className="w-5 h-5 mr-2 border-b-2 border-white rounded-full animate-spin"></div>
              Submitting Report...
            </div>
          ) : (
            'Submit Observer Report'
          )}
        </button>

        {completionRate < 80 && (
          <div className="p-4 mt-4 border border-red-200 rounded-lg bg-red-50">
            <p className="text-sm font-medium text-red-700">
              ⚠️ Minimum 80% completion required
            </p>
            <p className="mt-1 text-xs text-red-600">
              Please complete at least 80% of the checklist questions before submitting.
              Current completion: {completionRate}%
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Step4Review;